import React, {forwardRef, useEffect, useRef, useState} from 'react';
import {
  Platform,
  Pressable,
  StyleProp,
  StyleSheet,
  TextInput as RNTextInput,
  TextInputProps as RNTextInputProps,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';

import Icon from '../../components/Icon';
import {fontFamilyStyle} from '../../components/ui-controls/Text';
import theme from '../../style/theme';
import {
  BASE_FIELD_BORDER_WIDTH,
  FIELD_ICON_COLOR,
  FIELD_PLACEHOLDER_COLOR,
  fieldPalette,
} from '../tokens/fieldPalette';
import {control, field, radius, space} from '../tokens/spacing';

import FieldFrame, {fieldAccessibilityHint} from './FieldFrame';

/**
 * Form field primitive from the LMG UI Library "Field States" frame
 * (file JwxvfWAYFSi5JBQ67QYxOG, node 444:6223).
 *
 * The input chrome lives here: the box, the input, the visibility toggle and
 * the visual states (default, focus, hover, filled, error, disabled/prefilled).
 * The label, required marker, description and error line come from FieldFrame.
 *
 * Colours and border widths come from the per-brand field palette in
 * app/design-system/tokens/fieldPalette.ts. Its default is the LMG UI Library
 * Field States frame, not a brand primary: measured 2026-09-22 across the four
 * brand theme files under assets/common/theme, PRIMARY_COLOUR is #0054A6
 * (Bottlemart), #FFDD00 (SipnSave), #000000 (Harry Brown) and #A0CE66
 * (Thirsty Camel). Binding the focus ring to it would give SipnSave a yellow
 * ring on a white field, about 1.3:1 against the surface, so the field would
 * read as unfocused. Thirsty Camel overrides focus, hover and filled (DIS-1825).
 */

export type FormFieldVisualState = 'default' | 'focus' | 'hover' | 'filled' | 'error' | 'disabled';

export type FormFieldProps = {
  label?: string;
  required?: boolean;
  description?: string;
  /** Forces the error chrome. Pair with errorMessage when there is copy to show. */
  error?: boolean;
  errorMessage?: string;
  /**
   * Forces a specimen state for Storybook. Omit it and the chrome follows
   * focus, value, error, and editable.
   */
  state?: FormFieldVisualState;
  value?: string;
  defaultValue?: string;
  onChangeText?: (text: string) => void;
  placeholder?: string;
  editable?: boolean;
  secureTextEntry?: boolean;
  /** Eye control from the Field States specimens. */
  visibilityToggle?: boolean;
  /**
   * Content rendered inside the box, before the input: a leading icon, or an
   * icon and a fixed string such as a dial code.
   *
   * A node rather than an icon name, so a caller can pass a composite such as
   * the mobile field's flag and '+61'. The Billing Address search glyph does not
   * use this slot: `AddressPicker` draws its own glyph (DIS-1748, spec D2). It is
   * decorative by default; anything interactive supplied here owns its own
   * accessibility.
   */
  prefix?: React.ReactNode;
  /**
   * Spoken at the start of the input's accessibility hint. The prefix is hidden
   * from assistive technology, so a prefix that carries information, such as a
   * dial code, must be restated here.
   */
  prefixAccessibilityHint?: string;
  testID?: string;
} & Pick<
  RNTextInputProps,
  | 'autoCapitalize'
  | 'autoComplete'
  | 'autoCorrect'
  | 'keyboardType'
  | 'maxLength'
  | 'onBlur'
  | 'onFocus'
  | 'onSubmitEditing'
  | 'returnKeyType'
  | 'textContentType'
>;

export type FieldChrome = {
  borderColor: string;
  backgroundColor: string;
  color: string;
  borderWidth: number;
  /**
   * BASE_FIELD_BORDER_WIDTH minus borderWidth: 0 for a 1px border, -1 for a
   * 2px one. Applied as the box's margin, it keeps the box's footprint and its
   * content in place when the border width changes between states.
   */
  borderCompensation: number;
};

// The field chrome literals live in the per-brand field palette
// (app/design-system/tokens/fieldPalette.ts). FIELD_ICON_COLOR is re-exported
// here because AddressPicker imports it from this module.
export {FIELD_ICON_COLOR};

// SHORTCUT: 16 is a platform floor, not a type token. Ceiling: web inputs only;
// it raises theme.size.text.body2 to 16 and never lowers it. Upgrade path: keep
// the floor, and read the size itself from a field-input type token once
// DIS-1735 adds one (no current token is a 16px input size).
// Not a typographic choice: iOS Safari auto-zooms on focus below 16px. The
// brand token is the size everywhere else; on web it is only ever raised to
// this floor. See DIS-1345.
const WEB_NO_ZOOM_MIN_FONT_SIZE = 16;

const INPUT_FONT_SIZE = theme.size.text.body2;
const WEB_INPUT_FONT_SIZE = Math.max(INPUT_FONT_SIZE, WEB_NO_ZOOM_MIN_FONT_SIZE);

// The field's own input text, minus colour and layout: family, size, and the
// web no-zoom floor. AccountCreateScreenLoyaltyEnabled's '+61' prefix uses
// this directly (plus its own colour from fieldChrome) so it renders in the
// identical family, size and weight as the input text beside it and the two
// cannot drift apart.
export const fieldTextStyle: StyleProp<TextStyle> = [
  fontFamilyStyle,
  {fontSize: Platform.OS === 'web' ? WEB_INPUT_FONT_SIZE : INPUT_FONT_SIZE},
];

export function resolveFormFieldState({
  state,
  editable,
  error,
  focused,
  hovered = false,
  hasValue,
}: {
  state?: FormFieldVisualState;
  editable: boolean;
  error: boolean;
  focused: boolean;
  /** Web pointer over the box. Ignored when the brand palette has no hover state. */
  hovered?: boolean;
  hasValue: boolean;
}): FormFieldVisualState {
  if (state) {
    return state;
  }
  if (!editable) {
    return 'disabled';
  }
  if (error) {
    return 'error';
  }
  if (focused) {
    return 'focus';
  }
  if (hovered && fieldPalette.hover) {
    return 'hover';
  }
  if (hasValue) {
    return 'filled';
  }
  return 'default';
}

/**
 * The chrome for one visual state, from the brand's field palette. `hover`
 * falls back to `focus` when the palette has no hover state; only a forced
 * `state="hover"` specimen reaches that fallback.
 */
export function fieldChrome(state: FormFieldVisualState): FieldChrome {
  const chrome =
    state === 'hover' ? (fieldPalette.hover ?? fieldPalette.focus) : fieldPalette[state];
  if (!chrome) {
    throw new Error(`Unhandled form field state: ${String(state)}`);
  }
  return {...chrome, borderCompensation: BASE_FIELD_BORDER_WIDTH - chrome.borderWidth};
}

export type FormFieldRef = React.ElementRef<typeof RNTextInput>;

const FormField = forwardRef<FormFieldRef, FormFieldProps>(
  (
    {
      label,
      required = false,
      description,
      error = false,
      errorMessage,
      state,
      value,
      defaultValue = '',
      onChangeText,
      placeholder,
      editable = true,
      secureTextEntry = false,
      visibilityToggle = false,
      prefix,
      prefixAccessibilityHint,
      testID = 'form-field',
      autoCapitalize = 'none',
      autoCorrect = false,
      onBlur,
      onFocus,
      ...rest
    },
    ref,
  ) => {
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [concealed, setConcealed] = useState(secureTextEntry);
    const cancelReveal = useRef<(() => void) | null>(null);
    useEffect(() => () => cancelReveal.current?.(), []);
    const isControlled = value !== undefined;
    const text = isControlled ? value : uncontrolledValue;
    const visualState = resolveFormFieldState({
      state,
      editable,
      error,
      focused,
      hovered,
      hasValue: text.length > 0,
    });
    const chrome = fieldChrome(visualState);
    const isDisabled = visualState === 'disabled' || !editable;
    const hint =
      [
        prefixAccessibilityHint,
        fieldAccessibilityHint({required, description, error, errorMessage}),
      ]
        .filter(Boolean)
        .join('. ') || undefined;

    return (
      <FieldFrame
        label={label}
        required={required}
        description={description}
        error={error}
        errorMessage={errorMessage}
        testID={testID}>
        <View
          testID={`${testID}-box`}
          // Web only: react-native-web forwards mouse events on View, the same
          // pattern as HomepageCarousel.web.tsx. Native has no hover.
          {...(Platform.OS === 'web' && fieldPalette.hover ? hoverHandlers(setHovered) : null)}
          style={[
            styles.box,
            fieldBoxChromeStyle(chrome),
            Platform.OS === 'web' ? WEB_BOX_CLIP : null,
          ]}>
          {prefix ? (
            // accessibilityElementsHidden / importantForAccessibility keep a
            // decorative glyph or dial code out of the field's a11y name, which
            // the label already supplies.
            <View
              testID={`${testID}-prefix`}
              style={styles.prefix}
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants">
              {prefix}
            </View>
          ) : null}
          <RNTextInput
            {...rest}
            ref={ref}
            testID={`${testID}-input`}
            value={text}
            editable={!isDisabled}
            placeholder={placeholder}
            placeholderTextColor={FIELD_PLACEHOLDER_COLOR}
            autoCapitalize={autoCapitalize}
            autoCorrect={autoCorrect}
            secureTextEntry={visibilityToggle ? concealed : secureTextEntry}
            accessibilityLabel={label}
            accessibilityHint={hint}
            accessibilityState={{disabled: isDisabled}}
            onChangeText={next => {
              if (!isControlled) {
                setUncontrolledValue(next);
              }
              onChangeText?.(next);
            }}
            onFocus={event => {
              setFocused(true);
              if (Platform.OS === 'web') {
                cancelReveal.current?.();
                cancelReveal.current = revealFieldOnWebKeyboard(event.target, testID);
              }
              onFocus?.(event);
            }}
            onBlur={event => {
              setFocused(false);
              cancelReveal.current?.();
              cancelReveal.current = null;
              onBlur?.(event);
            }}
            style={[
              styles.input,
              fieldTextStyle,
              {color: chrome.color},
              visibilityToggle ? styles.inputWithToggle : null,
              Platform.OS === 'web' ? WEB_INPUT_NO_OUTLINE : null,
              Platform.OS === 'web' ? webAutofillGuard(chrome, text.length > 0) : null,
            ]}
          />
          {visibilityToggle ? (
            <Pressable
              testID={`${testID}-visibility`}
              accessibilityRole="button"
              accessibilityLabel={concealed ? 'Show value' : 'Hide value'}
              disabled={isDisabled}
              onPress={() => setConcealed(current => !current)}
              hitSlop={VISIBILITY_HIT_SLOP}
              style={styles.visibility}>
              <Icon
                name={concealed ? 'eye-slash' : 'eye'}
                iconStyle="regular"
                size={16}
                color={isDisabled ? fieldPalette.disabled.color : FIELD_ICON_COLOR}
              />
            </Pressable>
          ) : null}
        </View>
      </FieldFrame>
    );
  },
);

FormField.displayName = 'FormField';

// DIS-1859: on mobile web the browser scrolls only the focused <input> into
// view, so the error line below it stays under the soft keyboard. Once the
// keyboard has resized the visual viewport, scroll the whole field frame
// (label, box, description, error line) into view.
// Assumption, not measured: visualViewport fires `resize` more than once while
// the keyboard animates, so the scroll waits for KEYBOARD_SETTLE_MS of quiet.
// KEYBOARD_FALLBACK_MS covers focus moving between fields while the keyboard
// is already open (no resize fires), and browsers without visualViewport.
// block 'nearest' scrolls only as far as needed and not at all when the frame
// is already visible; 'center' would move the page on every focus.
const KEYBOARD_SETTLE_MS = 100;
const KEYBOARD_FALLBACK_MS = 300;

/**
 * Web only. Finds the field frame (the ancestor of `input` whose data-testid is
 * `frameTestID`, which react-native-web renders from testID) and scrolls it into
 * view after the keyboard settles. Returns a cancel function; call it on blur.
 */
export function revealFieldOnWebKeyboard(input: unknown, frameTestID: string): () => void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return () => {};
  }
  let node = input as HTMLElement | null;
  while (node && node.getAttribute?.('data-testid') !== frameTestID) {
    node = node.parentElement;
  }
  const frame = node;
  if (!frame || typeof frame.scrollIntoView !== 'function') {
    return () => {};
  }

  const viewport = window.visualViewport;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cancel = () => {
    clearTimeout(timer);
    viewport?.removeEventListener('resize', onResize);
  };
  // The listener stays until blur, so a keyboard that opens after the fallback
  // still triggers a reveal. A second reveal of a visible frame is a no-op.
  const reveal = () => frame.scrollIntoView({block: 'nearest'});
  function onResize() {
    clearTimeout(timer);
    timer = setTimeout(reveal, KEYBOARD_SETTLE_MS);
  }

  viewport?.addEventListener('resize', onResize);
  timer = setTimeout(reveal, KEYBOARD_FALLBACK_MS);
  return cancel;
}

// onMouseEnter/onMouseLeave are react-native-web View props, absent from the
// RN View types.
function hoverHandlers(setHovered: (hovered: boolean) => void): object {
  return {onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false)};
}

/**
 * The box's per-state chrome: colours, border width, and the negative margin
 * that absorbs a border wider than BASE_FIELD_BORDER_WIDTH. The margin key is
 * only present when it is non-zero, so a 1px state renders exactly the style
 * it rendered before DIS-1825. Otp uses it for its boxes too.
 */
export function fieldBoxChromeStyle(chrome: FieldChrome): ViewStyle {
  return {
    borderColor: chrome.borderColor,
    backgroundColor: chrome.backgroundColor,
    borderWidth: chrome.borderWidth,
    ...(chrome.borderCompensation !== 0 ? {margin: chrome.borderCompensation} : null),
  };
}

// PREFIX_GAP and the visibility icon's 16 are LMG UI Library Field States
// measurements. The painted field is control.fieldHeight (40). The toggle is
// the same painted size and reaches control.inputHeight (44) through hitSlop
// (DIS-1855). Input padding Y is space.s8 (DIS-1856).
const PREFIX_GAP = 8;
const VISIBILITY_HIT_SLOP = (control.inputHeight - control.fieldHeight) / 2;

// Chrome draws its `auto` focus ring whatever the outline width, over the
// field's own focus border, so outlineStyle 'none' is the load-bearing key.
// react-native-web accepts 'none'; RN's TextStyle type does not, hence the cast.
const WEB_INPUT_NO_OUTLINE = {outlineWidth: 0, outlineStyle: 'none'} as unknown as TextStyle;

// DIS-1863: browser autofill paints the <input> yellow (mobile Safari) or blue
// (desktop Chrome) over the box's own fill, so an autofilled field stops
// matching the one beside it. An inset shadow paints over that fill with the
// current state's own background, and -webkit-text-fill-color keeps the
// state's text colour. Both come from the brand palette, so every brand keeps
// its own colours and the error border (on the box) is untouched. Applied
// always on web, not under :-webkit-autofill, because react-native-web has no
// pseudo-class styles; without autofill it paints the colour already behind
// the input. The fill colour is set only while the field has a value, since
// it would also override the placeholder's grey.
function webAutofillGuard(chrome: FieldChrome, hasValue: boolean): TextStyle {
  return {
    boxShadow: `inset 0 0 0 1000px ${chrome.backgroundColor}`,
    ...(hasValue ? {WebkitTextFillColor: chrome.color} : null),
  } as unknown as TextStyle;
}

// DIS-1863: the input is a square box inside the rounded border. Its painted
// corners (autofill, or the guard above) cover the border's rounded corners,
// which reads as a broken border at the field's edges. Clip to the box.
const WEB_BOX_CLIP: ViewStyle = {overflow: 'hidden'};

const styles = StyleSheet.create({
  box: {
    minHeight: control.fieldHeight,
    borderWidth: 1,
    borderRadius: radius.control,
    flexDirection: 'row',
    alignItems: 'center',
  },
  // Left padding matches the input's own, so a prefixed field's content starts
  // on the same line as an unprefixed one's.
  prefix: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PREFIX_GAP,
    paddingLeft: field.paddingX,
  },
  input: {
    flex: 1,
    minHeight: control.fieldHeight,
    paddingHorizontal: field.paddingX,
    paddingVertical: space.s8,
  },
  inputWithToggle: {
    paddingRight: space.s4,
  },
  // outlineStyle is the load-bearing key: Chrome draws its `auto` focus ring
  // whatever the width, over the field's own focus border.
  visibility: {
    width: control.fieldHeight,
    height: control.fieldHeight,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default FormField;
